<template>
    <div>
        <PaymentMethodsList
            v-if="!createNewPaymentMethod"
            :payment-methods="paymentMethods"
            :payment-methods-loading="paymentMethodsLoading"
            :selected-payment-method-token="selectedPaymentMethodToken"
            @delete-payment-method="deletePaymentMethod"
            @change-default-payment-method="changeDefaultPaymentMethod"
            @create-payment-method="handleCreatePaymentMethod"
        />

        <PaymentMethodsCreate
            v-if="createNewPaymentMethod"
            :billing-address="billingAddress"
            :subscription-type="subscriptionType"
            @close="handleCloseCreatePaymentMethod"
        />
    </div>
</template>

<script setup lang="ts">
import useConfirm from '@/composables/useConfirm';
import { PropType, onMounted, ref } from 'vue';
import { IPaymentMethod } from '@/types/Subscription/IPaymentMethod';
import { useBraintreeClient } from '@/composables/useBraintree';
import BillingAddress from '@/types/Subscription/BillingAddress';
import { useStripeClient } from '@/composables/useStripe';

const props = defineProps({
    billingAddress: {
        required: true,
        type: Object as PropType<BillingAddress>,
    },
    subscriptionType: {
        required: true,
        type: String as PropType<'pro' | 'plugin'>,
    },
});

const { teamFeatureDisabled } = useFeatures();
const emit = defineEmits(['selectPaymentMethod', 'loading']);

const isStripeEnabled = !teamFeatureDisabled('stripe');

const {
    getPaymentMethods: braintreeGetPaymentMethods,
    setDefaultPaymentMethod: braintreeSetDefaultPaymentMethod,
    deletePaymentMethod: braintreeDeletePaymentMethod,
} = useBraintreeClient(props.subscriptionType);

const {
    getPaymentMethods: stripeGetPaymentMethods,
    setDefaultPaymentMethod: stripeSetDefaultPaymentMethod,
    deletePaymentMethod: stripeDeletePaymentMethod,
} = useStripeClient(props.subscriptionType);

onMounted(async () => {
    await fetchPaymentMethods();
});

const { show: showConfirmation } = useConfirm();
const createNewPaymentMethod = ref(false);
const paymentMethods = ref([] as IPaymentMethod[]);
const paymentMethodsLoading = ref(true);
const selectedPaymentMethodToken = ref('');

const handleCloseCreatePaymentMethod = async () => {
    if (createNewPaymentMethod.value) {
        createNewPaymentMethod.value = false;
        paymentMethodsLoading.value = true;
        await fetchPaymentMethods();
    }
};

const handleCreatePaymentMethod = () => {
    createNewPaymentMethod.value = true;
    emit('loading', true);
};

watch(
    () => paymentMethodsLoading.value,
    (value) => {
        emit('loading', value);
    }
);

const fetchPaymentMethods = async () => {
    try {
        const response = isStripeEnabled
            ? await stripeGetPaymentMethods()
            : await braintreeGetPaymentMethods();

        if (response.data.success === false) {
            throw new Error('Failed to fetch payment methods');
        }
        paymentMethods.value = response.data.data;
        paymentMethods.value.map((paymentMethod: IPaymentMethod) => {
            if (paymentMethod.default) {
                selectPaymentMethod(paymentMethod);
            }
        });
    } catch (e) {
        create({
            title: 'Error',
            text: 'Failed to fetch payment methods. Please try again or contact us for support.',
            type: 'error',
        });
    } finally {
        paymentMethodsLoading.value = false;
    }
};

const changeDefaultPaymentMethod = async (
    selectedPaymentMethod: IPaymentMethod
) => {
    if (selectedPaymentMethod.default) {
        return;
    }

    try {
        paymentMethodsLoading.value = true;

        const response = isStripeEnabled
            ? await stripeSetDefaultPaymentMethod(selectedPaymentMethod.token)
            : await braintreeSetDefaultPaymentMethod(
                  selectedPaymentMethod.token
              );

        if (response.data.success) {
            paymentMethods.value.map((paymentMethod: IPaymentMethod) => {
                if (paymentMethod.token === selectedPaymentMethod.token) {
                    paymentMethod.default = true;
                } else {
                    paymentMethod.default = false;
                }

                return paymentMethod;
            });
            await selectPaymentMethod(selectedPaymentMethod);
        } else {
            throw new Error('Failed to set default payment method');
        }
    } catch (e) {
        create({
            title: 'Error',
            text: 'Failed to set default payment method. Please try again or contact us for support.',
            type: 'error',
        });
    } finally {
        paymentMethodsLoading.value = false;
    }
};

const deletePaymentMethod = async (selectedPaymentMethod: IPaymentMethod) => {
    const confirmed = await showConfirmation({
        header: 'Delete card',
        message: `Do you really want to delete this card?`,
        type: 'danger',
    });

    if (!confirmed) {
        return;
    }

    try {
        paymentMethodsLoading.value = true;
        const response = isStripeEnabled
            ? await stripeDeletePaymentMethod(selectedPaymentMethod.token)
            : await braintreeDeletePaymentMethod(selectedPaymentMethod.token);

        if (response.data.success) {
            paymentMethods.value = paymentMethods.value.filter(
                (paymentMethod: IPaymentMethod) => {
                    return paymentMethod.token !== selectedPaymentMethod.token;
                }
            );

            create({
                title: 'Success',
                text: 'The payment method has been deleted successfully.',
                type: 'success',
            });
        } else {
            throw new Error('Failed to delete the card');
        }
    } catch {
        create({
            title: 'Error',
            text: 'Failed to delete the payment method. Please try again or contact us for support.',
            type: 'error',
        });
    } finally {
        paymentMethodsLoading.value = false;
    }
};

const selectPaymentMethod = async (paymentMethod: IPaymentMethod) => {
    selectedPaymentMethodToken.value = paymentMethod.token;
    emit('selectPaymentMethod', paymentMethod);
};

defineExpose({
    handleCloseCreatePaymentMethod,
});
</script>
